import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { UserService } from '../../../services/user.service';
import { EstudianteService } from '../../../services/estudiante.service';
import { User } from '../../../models/user.model';
import { Estudiante } from '../../../models/estudiante.model';

@Component({
  selector: 'app-usuario-form',
  templateUrl: './usuario-form.component.html',
  styleUrls: ['./usuario-form.component.css']
})
export class UsuarioFormComponent implements OnInit {
  userForm: FormGroup;
  isEditMode = false;
  userId: number | null = null;
  loading = false;
  saving = false;
  error: string | null = null;
  
  // For nro_libreta search
  searchingEstudiante = false;
  estudiante: Estudiante | null = null;
  estudianteNotFound = false;
  
  // Show/hide password
  showPassword = false;
  
  // Role options
  roles = [
    { value: 'admin', label: 'Administrador', description: 'Acceso completo al sistema' },
    { value: 'mesa', label: 'Mesa de examen', description: 'Gestión de exámenes y calificaciones' },
    { value: 'recepcion', label: 'Recepción', description: 'Registro de estudiantes y pagos' },
    { value: 'instructor', label: 'Instructor', description: 'Consulta de estudiantes asignados' },
    { value: 'readonly', label: 'Solo lectura', description: 'Consulta únicamente' }
  ];

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private userService: UserService,
    private estudianteService: EstudianteService
  ) {
    this.userForm = this.fb.group({
      username: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(50)]],
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.minLength(8)]],
      role: ['readonly', Validators.required],
      nroLibreta: [null]
    });
  }

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.isEditMode = true;
      this.userId = parseInt(id);
      this.loadUser();
      
      // Remove password requirement in edit mode
      this.userForm.get('password')?.clearValidators();
      this.userForm.get('password')?.updateValueAndValidity();
    } else {
      // Password required in create mode
      this.userForm.get('password')?.setValidators([
        Validators.required,
        Validators.minLength(8),
        Validators.pattern(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/)
      ]);
    }
  }

  loadUser(): void {
    if (!this.userId) return;
    
    this.loading = true;
    this.error = null;
    
    this.userService.getUser(this.userId).subscribe(
      user => {
        this.userForm.patchValue({
          username: user.username,
          email: user.email,
          role: user.role,
          nroLibreta: user.nroLibreta
        });
        
        // Load estudiante if exists
        if (user.nroLibreta) {
          this.searchEstudiante(user.nroLibreta);
        }
        
        this.loading = false;
      },
      error => {
        console.error('Error loading user:', error);
        this.error = 'Error al cargar el usuario';
        this.loading = false;
      }
    );
  }

  /**
   * Search estudiante by nro_libreta
   */
  searchEstudiante(nroLibreta: number | null): void {
    if (!nroLibreta || nroLibreta <= 0) {
      this.estudiante = null;
      this.estudianteNotFound = false;
      return;
    }
    
    this.searchingEstudiante = true;
    this.estudianteNotFound = false;
    this.error = null;
    
    this.estudianteService.getEstudiante(nroLibreta).subscribe(
      estudiante => {
        this.estudiante = estudiante;
        this.searchingEstudiante = false;
      },
      error => {
        console.error('Error searching estudiante:', error);
        this.estudiante = null;
        this.estudianteNotFound = true;
        this.searchingEstudiante = false;
      }
    );
  }

  /**
   * Clear nro_libreta field
   */
  clearNroLibreta(): void {
    this.userForm.patchValue({ nroLibreta: null });
    this.estudiante = null;
    this.estudianteNotFound = false;
  }

  /**
   * Generate secure password
   */
  generatePassword(): void {
    const length = 12;
    const lowercase = 'abcdefghijklmnopqrstuvwxyz';
    const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const numbers = '0123456789';
    const special = '!@#$%^&*()_+-=[]{}|;:,.<>?';
    
    const allChars = lowercase + uppercase + numbers + special;
    
    let password = '';
    
    // Ensure at least one of each required character type
    password += lowercase[Math.floor(Math.random() * lowercase.length)];
    password += uppercase[Math.floor(Math.random() * uppercase.length)];
    password += numbers[Math.floor(Math.random() * numbers.length)];
    password += special[Math.floor(Math.random() * special.length)];
    
    // Fill the rest randomly
    for (let i = password.length; i < length; i++) {
      password += allChars[Math.floor(Math.random() * allChars.length)];
    }
    
    // Shuffle the password
    password = password.split('').sort(() => Math.random() - 0.5).join('');
    
    this.userForm.patchValue({ password });
    this.showPassword = true;
  }

  /**
   * Toggle password visibility
   */
  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  /**
   * Copy password to clipboard
   */
  copyPasswordToClipboard(): void {
    const password = this.userForm.get('password')?.value;
    if (password) {
      navigator.clipboard.writeText(password).then(() => {
        alert('Contraseña copiada al portapapeles');
      }).catch(err => {
        console.error('Error copying to clipboard:', err);
        alert('Error al copiar la contraseña');
      });
    }
  }

  /**
   * Get role description
   */
  getRoleDescription(roleValue: string): string {
    const role = this.roles.find(r => r.value === roleValue);
    return role?.description || '';
  }

  /**
   * Submit form
   */
  onSubmit(): void {
    if (this.userForm.invalid) {
      Object.keys(this.userForm.controls).forEach(key => {
        this.userForm.get(key)?.markAsTouched();
      });
      return;
    }
    
    this.saving = true;
    this.error = null;
    
    const formData = {
      ...this.userForm.value,
      nroLibreta: this.userForm.value.nroLibreta || null
    };
    
    // Remove password if empty in edit mode
    if (this.isEditMode && !formData.password) {
      delete formData.password;
    }
    
    const request = this.isEditMode
      ? this.userService.updateUser(this.userId!, formData)
      : this.userService.createUser(formData);
    
    request.subscribe(
      user => {
        const message = this.isEditMode
          ? 'Usuario actualizado correctamente'
          : 'Usuario creado correctamente';
        
        alert(message);
        this.router.navigate(['/usuarios']);
      },
      error => {
        console.error('Error saving user:', error);
        this.error = error.error?.error || 'Error al guardar el usuario';
        this.saving = false;
      }
    );
  }

  /**
   * Cancel and go back
   */
  onCancel(): void {
    if (confirm('¿Está seguro que desea cancelar? Los cambios no guardados se perderán.')) {
      this.router.navigate(['/usuarios']);
    }
  }

  /**
   * Check if field has error
   */
  hasError(field: string, error?: string): boolean {
    const control = this.userForm.get(field);
    if (!control) return false;
    
    if (error) {
      return control.hasError(error) && control.touched;
    }
    return control.invalid && control.touched;
  }

  /**
   * Get error message for field
   */
  getErrorMessage(field: string): string {
    const control = this.userForm.get(field);
    if (!control || !control.errors || !control.touched) return '';
    
    if (control.errors['required']) return 'Este campo es requerido';
    if (control.errors['email']) return 'Email inválido';
    if (control.errors['minlength']) {
      const minLength = control.errors['minlength'].requiredLength;
      return `Mínimo ${minLength} caracteres`;
    }
    if (control.errors['maxlength']) {
      const maxLength = control.errors['maxlength'].requiredLength;
      return `Máximo ${maxLength} caracteres`;
    }
    if (control.errors['pattern']) {
      return 'La contraseña debe contener mayúsculas, minúsculas y números';
    }
    
    return 'Campo inválido';
  }
}